'use client';

import { ChevronRight } from 'lucide-react';
import { RefObject, useRef } from 'react';

import { clsx } from 'clsx';
import Image, { StaticImageData } from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useOnClickOutside } from 'usehooks-ts';

import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';

import { categories, subCategories } from '../../model/data/data';
import { changeActiveImage, changeActiveValue, closeCategoriesModal } from '../../model/slice/categoriesModalSlice';
import cls from './CategoriesDesktop.module.scss';

type CategoriesModalProps = {
  buttonRef?: RefObject<HTMLElement>;
};

export const CategoriesDesktop = ({ buttonRef }: CategoriesModalProps) => {
  const dispatch = useAppDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const activeValue = useAppSelector((state) => state.categoriesModal.activeValue);
  const open = useAppSelector((state) => state.categoriesModal.open);
  const activeImage = useAppSelector((state) => state.categoriesModal.activeImage);
  const { push } = useRouter();

  const categoryMouseOnHandler = (value: string, image: StaticImageData) => {
    dispatch(changeActiveValue(value));
    dispatch(changeActiveImage(image));
  };

  const subCategoryMouseOnHandler = (image: StaticImageData) => {
    dispatch(changeActiveImage(image));
  };

  const onCloseModal = () => {
    dispatch(changeActiveValue(null));
    dispatch(closeCategoriesModal());
  };

  useOnClickOutside([wrapperRef, buttonRef!], onCloseModal);

  return (
    <nav
      ref={wrapperRef}
      data-testid="categories-desktop"
      onMouseLeave={onCloseModal}
      className={clsx(cls.wrapper, { [cls.open]: open })}
    >
      <div className={cls.aside}>
        {categories.map((el) => (
          <Link
            data-testid={'category-menu-link-desktop'}
            aria-label={`Перейти на страницу ${el.label}`}
            prefetch={false}
            key={el.url}
            href={el.url}
            onMouseOver={() => categoryMouseOnHandler(el.value, el.image)}
            className={clsx(cls.link, { [cls.active]: activeValue === el.value })}
          >
            <span className={cls.icon}>
              <Image
                className={cls.default}
                src={el.iconDefault}
                fill
                alt={`Иконка категории ${el.label}`}
              />
              <Image
                className={cls.hovered}
                src={el.iconHover}
                fill
                alt={`Иконка категории ${el.label}`}
              />
            </span>

            <span> {el.label}</span>

            <ChevronRight size={20} />
          </Link>
        ))}
      </div>

      <div className={cls.desktop}>
        {activeValue && (
          <div className={cls.content}>
            <div className={cls.links}>
              {subCategories[activeValue].map((item, index) => (
                <div
                  role={'link'}
                  aria-label={`Перейти на страницу ${item.url}`}
                  data-testid={'sub-category-menu-link-desktop'}
                  key={index}
                  onClick={() => {
                    push(item.url);
                    onCloseModal();
                  }}
                  onMouseOver={() => subCategoryMouseOnHandler(item.image)}
                  className={cls.sublink}
                >
                  <span className={cls.icon}>
                    <Image
                      className={cls.default}
                      src={item.iconDefault}
                      fill
                      alt={`Иконка категории ${item.label}`}
                    />
                    <Image
                      className={cls.hovered}
                      src={item.iconHover}
                      fill
                      alt={`Иконка категории ${item.label}`}
                    />
                  </span>
                  {item.label}
                </div>
              ))}
            </div>
            {activeImage && (
              <div className={cls.preview}>
                <Image
                  src={activeImage}
                  alt={'Изображение'}
                  fill
                />
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
